import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Sponsor, VideoAd } from '../_interfaces';
import { videoAdStateInterface } from './video-ads.service';
import { SsrlocalstoreService } from './ssr-localstore.service';
import { differenceInSeconds, subHours } from 'date-fns';
import { HttpPromiseService } from './http-promise.service';
import { environment } from 'src/environments/environment';
import { v4 } from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class SsrAdService {
    private readonly keyName: string = 'T1VideoAd';

    public videoAdState: videoAdStateInterface = {
        ad_free_seconds: 5,
        ad_value_required: 100,
        last_ad_timestamp: subHours(new Date(), 1).toISOString()
    };

    public videoAdSponsor: BehaviorSubject<Sponsor | null> = new BehaviorSubject<Sponsor | null>(null);

    private videoAdCollection: VideoAd[] = [];
    private ad_pool: BehaviorSubject<Sponsor[]> = new BehaviorSubject<Sponsor[]>([]);

    public readonly adPool$ = this.ad_pool.asObservable();

    constructor(
        private ssr: SsrlocalstoreService,
        private http: HttpPromiseService,
    ) { }

    setSponsorPool(sponsors: Sponsor[]): void {
        this.ad_pool.next(sponsors ?? []);
    }

    get__videoAd(): VideoAd[] {
        if (this.videoAdState.last_ad_id && this.videoAdCollection?.length) {
            const ad_batch: VideoAd[] = [];
            let ad_value_counter: number = 0;

            while (ad_value_counter < this.videoAdState.ad_value_required) {
                const last_ad = (ad: VideoAd) => ad.id === this.videoAdState.last_ad_id;
                const previous_idx = this.videoAdCollection.findIndex(last_ad);
                const next_ad = this.videoAdCollection[previous_idx + 1] || this.videoAdCollection[previous_idx];

                ad_batch.push(next_ad);
                ad_value_counter += next_ad.value;

                this.update__videoState(next_ad, this.isAddFree() ? true : false);
            }

            this.set__localStore();
            this.videoAdSponsor.next(ad_batch?.[0]?.sponsor);

            return ad_batch;
        } else if (!this.videoAdState.last_ad_id && this.videoAdCollection?.length) {
            this.update__videoState(this.videoAdCollection[0], this.isAddFree() ? true : false);
            this.set__localStore();
            this.videoAdSponsor.next(this.videoAdCollection?.[0]?.sponsor);

            return [this.videoAdCollection[0]];
        } else {
            return [];
        }
    }

    private set__localStore() {
        return this.ssr.setItem(this.keyName, JSON.stringify(this.videoAdState));
    }

    private update__videoState(ad: VideoAd, update_timestamp: boolean = true) {
        this.videoAdState.last_ad_id = ad.id;
        if (update_timestamp) {
            this.videoAdState.last_ad_timestamp = new Date().toISOString();
        }
    }

    isAddFree(): boolean {
        const diff = differenceInSeconds(new Date(), new Date(this.videoAdState.last_ad_timestamp));
        return diff <= this.videoAdState.ad_free_seconds ? true : false;
    }

    getSponsor(sponsor_key: string = ''): Sponsor {
        const pool = this.ad_pool.value ?? [];

        if (pool.length <= 0) {
            return {} as Sponsor;
        }

        const top_sponsors = pool.filter(sponsor => sponsor.key === sponsor_key && sponsor.enabled);

        if (top_sponsors.length <= 0) {
            return {} as Sponsor;
        }

        if (this.ssr.getItem(sponsor_key)) {
            const key = this.ssr.getItem(sponsor_key) as string;
            const add_one = (parseInt(key, 10) + 1).toString();
            this.ssr.setItem(sponsor_key, add_one);
        } else {
            this.ssr.setItem(sponsor_key, '0');
        }

        const sponsor_count = +(this.ssr.getItem(sponsor_key) || 0);
        const sorted_sponsors = top_sponsors.sort((a, b) => a.priority - b.priority);
        const rotation = sponsor_count % sorted_sponsors.length;

        return sorted_sponsors[rotation] ?? ({} as Sponsor);
    }

    initFingerPrint() {
        if (!this.ssr.getItem('golfOne-fingerprint')) {
            this.ssr.setItem('golfOne-fingerprint', `web-${v4()}`);
        }
    }

    async postEvent(event_key: string, event = {}) {
        if (this.ssr.isClientSide && location.hostname === 'localhost') {
            return;
        }

        try {
            const payload = this.createEvent(event_key, event);
            return this.http.post(environment.ANALYTIC_ENDPOINT, payload, { type: 'auth' })
                .catch((err) => {
                    console.error(err);
                    throw err;
                });

        } catch (err) {
            throw err;
        }
    }

    postVideoEvent(
        payload: {
            video_url: string;
            video_location: string;
            video_location_id: string;
            sponsor_campaign_id: string | null;
        }
    ): void {
        this.postEvent('video_load', payload);
        setTimeout(() => this.postEvent('video_view', payload), 30000);
    }

    createEvent(event_key: string, event: { [key: string]: any }): { [key: string]: any } {
        const meta = this.buildMeta();
        const events: Array<{ [key: string]: any }> = [{ event_key, event, meta }];
        const payload: { [key: string]: any } = { events };

        return payload;
    }

    private buildMeta(team_id?: string) {
        const meta: { [key: string]: any } = {
            local_time: new Date().toISOString(),
            language: navigator.language,
            locale: navigator.language.replace(/-/g, '_'),
            os: this.getOS(),
            team: {
                activeId: team_id
            },
            advertiserid: ''
        };

        return meta;
    }

    private getOS() {
        const nAgt = navigator.userAgent;
        const clientStrings: Array<{ [key: string]: any }> = [
            { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
            { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
            { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
            { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
            { s: 'Windows Vista', r: /Windows NT 6.0/ },
            { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
            { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
            { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
            { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
            { s: 'Windows 98', r: /(Windows 98|Win98)/ },
            { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
            { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
            { s: 'Windows CE', r: /Windows CE/ },
            { s: 'Windows 3.11', r: /Win16/ },
            { s: 'Android', r: /Android/ },
            { s: 'Open BSD', r: /OpenBSD/ },
            { s: 'Sun OS', r: /SunOS/ },
            { s: 'Linux', r: /(Linux|X11)/ },
            { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
            { s: 'Mac OS X', r: /Mac OS X/ },
            { s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
            { s: 'QNX', r: /QNX/ },
            { s: 'UNIX', r: /UNIX/ },
            { s: 'BeOS', r: /BeOS/ },
            { s: 'OS/2', r: /OS\/2/ },
            { s: 'Search Bot', r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ }
        ];

        const os = clientStrings.find((val) => val.r.test(nAgt));
        return os ? os.s : '';
    }
}
